
import React, { useEffect, useState } from 'react';
import AxiosInstance from '../../AxiosInstance';
import { getImageUrl } from '../../utils/imageUrl';
import { formatXaf, formatEurFromXaf } from '../../utils/currency';
import './agent.css';
import { useAuth } from "../../AuthContext";
import { useNavigate } from "react-router-dom";

export default function AgentDashboard() {

  // ===== États =====
  const [allAgents, setAllAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [stats, setStats] = useState({});
  const [profil, setProfil] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentStats, setAgentStats] = useState(null);
  const [agentServices, setAgentServices] = useState([]);

  const [activeTab, setActiveTab] = useState('services');

  const [expandedServices, setExpandedServices] = useState({});
  const [serviceDetailsCache, setServiceDetailsCache] = useState({});

  const [contactNotesOpen, setContactNotesOpen] = useState(false);
  const [currentServiceForNotes, setCurrentServiceForNotes] = useState(null);
  const [contactNotes, setContactNotes] = useState([]);
  const [contactNotesLoading, setContactNotesLoading] = useState(false);
  const [newContactNote, setNewContactNote] = useState('');

  const [updateStatusOpen, setUpdateStatusOpen] = useState(false);
  const [currentServiceForStatus, setCurrentServiceForStatus] = useState(null);
  const [newServiceStatus, setNewServiceStatus] = useState('not_started');
  const [statusUpdateNotes, setStatusUpdateNotes] = useState('');

  const [contactClientOpen, setContactClientOpen] = useState(false);
  const [currentServiceForContact, setCurrentServiceForContact] = useState(null);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactChannel, setContactChannel] = useState('email');
  const [contactClientLoading, setContactClientLoading] = useState(false);

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyStatistics, setHistoryStatistics] = useState(null);

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // ===== Tarif mandataire (XAF) =====
  const DEFAULT_AGENT_PRICE_XAF = 8855; // ≈ 13,50 EUR, défaut backend
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [priceAgent, setPriceAgent] = useState(null);
  const [priceInput, setPriceInput] = useState('');
  const [priceError, setPriceError] = useState('');
  const [priceLoading, setPriceLoading] = useState(false);

  function openPriceModal(agent, e) {
    if (e) e.stopPropagation();
    setPriceAgent(agent);
    setPriceInput(String(agent.basePrice ?? DEFAULT_AGENT_PRICE_XAF));
    setPriceError('');
    setPriceModalOpen(true);
  }

  function closePriceModal() {
    setPriceModalOpen(false);
    setPriceAgent(null);
    setPriceInput('');
    setPriceError('');
    setPriceLoading(false);
  }

  function handlePriceInputChange(value) {
    setPriceInput(value);
    if (priceError) setPriceError('');
  }

  function validatePriceXaf(raw) {
    const trimmed = String(raw).trim().replace(/\s/g, '').replace(',', '.');
    if (trimmed === '') return { error: 'Veuillez saisir un tarif.' };
    const value = Number(trimmed);
    if (!Number.isFinite(value)) return { error: 'Le tarif doit être un nombre.' };
    if (!Number.isInteger(value)) return { error: 'Le tarif doit être un nombre entier de francs CFA.' };
    if (value <= 0) return { error: 'Le tarif doit être un entier strictement positif.' };
    return { value };
  }

  async function submitPriceUpdate() {
    if (!priceAgent) return;
    const { value, error } = validatePriceXaf(priceInput);
    if (error) {
      setPriceError(error);
      return;
    }
    setPriceLoading(true);
    try {
      const res = await AxiosInstance.put(`/api/agents/${priceAgent._id}`, { basePrice: value });
      const result = res.data;
      if (result.success) {
        showSuccess(`Tarif de ${priceAgent.userId?.name || 'l\'agent'} mis à jour : ${formatXaf(value)} (${formatEurFromXaf(value)})`);
        closePriceModal();
        await loadAgents();
      } else {
        setPriceError(result.message || 'Erreur lors de la mise à jour du tarif');
      }
    } catch (err) {
      console.error('Erreur mise à jour tarif agent:', err);
      setPriceError(err.response?.data?.message || "Impossible de mettre à jour le tarif");
    } finally {
      setPriceLoading(false);
    }
  }

  const parsedPrice = validatePriceXaf(priceInput);


  const openAddModal = () => setIsAddModalOpen(true);
  const closeAddModal = () => setIsAddModalOpen(false);
  const openUpdateModal = () => setIsUpdateModalOpen(true);
  const closeUpdateModal = () => setIsUpdateModalOpen(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const thStyle = {
    textAlign: "left",
    padding: "10px",
    borderBottom: "2px solid #ccc"
  };

  const tdStyle = {
    padding: "10px",
    verticalAlign: "middle"
  };

  const avatarStyle = {
    width: "35px",
    height: "35px",
    borderRadius: "50%",
    backgroundColor: "#edf2f7",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold"
  };

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999
  };

  const modalStyle = {
    background: "#fff",
    padding: "25px",
    borderRadius: "8px",
    width: "400px",
    maxWidth: "95%",
    boxShadow: "0 0 10px rgba(0,0,0,0.2)"
  };

  const inputStyle = {
    width: "100%",
    padding: "8px",
    margin: "8px 0 16px",
    border: "1px solid #ccc",
    borderRadius: "5px"
  };

  // ===== Effets init =====
  useEffect(() => {
    loadStatistics();
    loadAgents();
  }, []);
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);
  useEffect(() => {
    AxiosInstance.get('/api/users/profile')
      .then(response => setProfil(response.data))
      .catch(error => console.error(error));
  }, []);

  useEffect(() => {
    if (errorMessage) {
      const t = setTimeout(() => setErrorMessage(''), 5000);
      return () => clearTimeout(t);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  // ===== Messages =====
  function showError(message) {
    setErrorMessage(message);
  }
  function showSuccess(message) {
    setSuccessMessage(message);
  }

  // ===== Chargement statistiques =====
  async function loadStatistics() {
    try {
      const res = await AxiosInstance.get('/api/agents/statistics/overview');
      const result = res.data;
      if (result.success) setStats(result.data || {});
    } catch (err) {
      console.error('Erreur chargement statistiques:', err);
    }
  }

  // ===== Chargement agents =====
  async function loadAgents() {
    try {
      setLoadingAgents(true);
      const res = await AxiosInstance.get('/api/agents');
      const result = res.data;
      if (result.success) setAllAgents(result.data || []);
      else showError('Erreur lors du chargement des agents');
    } catch (err) {
      console.error('Erreur:', err);
      showError("Impossible de charger les agents");
    } finally {
      setLoadingAgents(false);
    }
  }

  // ===== Filtres & recherche =====
  function applyFilters(agentsList) {
    return agentsList.filter(agent => {
      const statusMatch = !statusFilter || String(agent.isActive) === statusFilter;
      const specialtyMatch = !specialtyFilter || (agent.specialties && agent.specialties.includes(specialtyFilter));
      return statusMatch && specialtyMatch;
    });
  }

  const filteredAgents = applyFilters(
    allAgents.filter(agent => {
      const user = agent.userId || {};
      const term = searchTerm.toLowerCase();
      return (
        (user.name || '').toLowerCase().includes(term) ||
        (user.email || '').toLowerCase().includes(term) ||
        (String(agent._id) || '').toLowerCase().includes(term)
      );
    })
  );

  // ===== Modal agent =====
  async function openAgentModal(agentId) {
    const a = allAgents.find(x => x._id === agentId);
    if (!a) return;
    setSelectedAgent(a);
    setAgentModalOpen(true);
    document.body.style.overflow = 'hidden';
    setAgentLoading(true);
    setAgentStats(null);
    setAgentServices([]);
    setActiveTab('services');
    try {
      await Promise.all([loadAgentStats(agentId), loadAgentServices(agentId)]);
    } catch (err) {
      console.error('Erreur chargement modal:', err);
      showError("Erreur lors du chargement des détails");
    } finally {
      setAgentLoading(false);
    }
  }

  function closeAgentModal() {
    setAgentModalOpen(false);
    document.body.style.overflow = 'auto';
    setSelectedAgent(null);
    setAgentServices([]);
    setServiceDetailsCache({});
    setExpandedServices({});
  }

  async function loadAgentStats(agentId) {
    try {
      const res = await AxiosInstance.get(`/api/agents/${agentId}/stats`);
      const result = res.data;
      if (result.success) setAgentStats(result.data || {});
    } catch (err) {
      console.error('Erreur stats agent:', err);
    }
  }

  async function loadAgentServices(agentId) {
    try {
      const res = await AxiosInstance.get(`/api/agents/${agentId}/services`);
      const result = res.data;
      if (result.success) setAgentServices(result.data || []);
      else setAgentServices([]);
    } catch (err) {
      console.error('Erreur services:', err);
      setAgentServices([]);
    }
  }

  // ===== Toggle service expand & details load =====
  async function toggleServiceExpand(serviceDetailId) {
    setExpandedServices(prev => ({ ...prev, [serviceDetailId]: !prev[serviceDetailId] }));
    if (!serviceDetailsCache[serviceDetailId]) await loadServiceDetails(serviceDetailId);
  }

  async function loadServiceDetails(serviceDetailId) {
    try {
      const res = await AxiosInstance.get(`/api/documents-for-agents/service/${serviceDetailId}/stats`);
      const result = res.data;
      if (result.success) {
        setServiceDetailsCache(prev => ({
          ...prev,
          [serviceDetailId]: { etapesDetails: result.data.etapesDetails || [] }
        }));
      } else {
        setServiceDetailsCache(prev => ({ ...prev, [serviceDetailId]: { etapesDetails: [] } }));
      }
    } catch (err) {
      console.error('Erreur détails service:', err);
      setServiceDetailsCache(prev => ({ ...prev, [serviceDetailId]: { etapesDetails: [] } }));
    }
  }

  async function loadEtapeDocuments(etapeId, serviceDetailId) {
    try {
      const res = await AxiosInstance.get(`/api/documents-for-agents/service/${serviceDetailId}/etapes/${etapeId}/documents`);
      const result = res.data;
      if (result.success) {
        setServiceDetailsCache(prev => {
          const copy = { ...prev };
          copy[serviceDetailId] = copy[serviceDetailId] || { etapesDetails: [] };
          copy[serviceDetailId].documents = copy[serviceDetailId].documents || {};
          copy[serviceDetailId].documents[etapeId] = result.data;
          return copy;
        });
      }
    } catch (err) {
      console.error('Erreur chargement documents:', err);
    }
  }

  function downloadDocument(docId) {
    window.open(getImageUrl(`/api/documents-for-agents/${docId}/download`), '_blank');
  }
  function downloadAllDocuments(serviceDetailId) {
    window.open(getImageUrl(`/api/documents-for-agents/service/${serviceDetailId}/download-all`), '_blank');
  }

  // ===== Contact notes =====
  async function openContactNotesModal(serviceDetailId, e) {
    if (e) e.stopPropagation();
    setCurrentServiceForNotes(serviceDetailId);
    setContactNotesOpen(true);
    await loadContactNotes(serviceDetailId);
  }

  async function loadContactNotes(serviceDetailId) {
    if (!selectedAgent) return;
    setContactNotesLoading(true);
    try {
      const res = await AxiosInstance.get(`/api/agents/${selectedAgent._id}/services/${serviceDetailId}/contact-notes`);
      const result = res.data;
      if (result.success) setContactNotes(result.data.contactNotes || []);
      else setContactNotes([]);
    } catch (err) {
      console.error('Erreur chargement notes:', err);
      setContactNotes([]);
    } finally {
      setContactNotesLoading(false);
    }
  }

  async function addContactNote() {
    if (!newContactNote.trim() || !selectedAgent || !currentServiceForNotes) {
      showError("Veuillez entrer une note");
      return;
    }
    try {
      const res = await AxiosInstance.post(`/api/agents/${selectedAgent._id}/services/${currentServiceForNotes}/contact-notes`, {
        note: newContactNote,
        addedBy: null
      });
      const result = res.data;
      if (result.success) {
        showSuccess('Note ajoutée avec succès');
        setNewContactNote('');
        await loadContactNotes(currentServiceForNotes);
        setAgentServices(prev => prev.map(s => s.serviceDetailId === currentServiceForNotes ? { ...s, contactNotesCount: (s.contactNotesCount || 0) + 1 } : s));
      } else showError("Erreur lors de l'ajout de la note");
    } catch (err) {
      console.error('Erreur ajout note:', err);
      showError("Impossible d'ajouter la note");
    }
  }

  function closeContactNotesModal() {
    setContactNotesOpen(false);
    setCurrentServiceForNotes(null);
    setContactNotes([]);
  }

  // ===== Update status =====
  function openUpdateStatusModal(serviceDetailId, e) {
    if (e) e.stopPropagation();
    setCurrentServiceForStatus(serviceDetailId);
    const s = agentServices.find(x => x.serviceDetailId === serviceDetailId);
    if (s) setNewServiceStatus(s.agentStatus || 'not_started');
    setUpdateStatusOpen(true);
  }

  async function submitStatusUpdate() {
    if (!currentServiceForStatus || !selectedAgent || !newServiceStatus) {
      showError('Données manquantes');
      return;
    }
    try {
      const res = await AxiosInstance.put(`/api/agents/${selectedAgent._id}/services/${currentServiceForStatus}/status`, {
        status: newServiceStatus,
        notes: statusUpdateNotes || undefined
      });
      const result = res.data;
      if (result.success) {
        showSuccess('Statut mis à jour avec succès');
        setUpdateStatusOpen(false);
        setStatusUpdateNotes('');
        setCurrentServiceForStatus(null);
        await loadAgentServices(selectedAgent._id);
      } else showError(result.message || 'Erreur lors de la mise à jour');
    } catch (err) {
      console.error('Erreur mise à jour statut:', err);
      showError("Impossible de mettre à jour le statut");
    }
  }

  function closeUpdateStatusModal() {
    setUpdateStatusOpen(false);
    setStatusUpdateNotes('');
    setCurrentServiceForStatus(null);
  }

  // ===== Contact Client =====
  function openContactClientModal(serviceDetailId, e) {
    if (e) e.stopPropagation();
    setCurrentServiceForContact(serviceDetailId);
    setContactSubject('');
    setContactMessage('');
    setContactChannel('email');
    setContactClientOpen(true);
  }

  function closeContactClientModal() {
    setContactClientOpen(false);
    setCurrentServiceForContact(null);
    setContactSubject('');
    setContactMessage('');
    setContactChannel('email');
  }

  async function submitContactClient() {
    if (!contactMessage.trim() || !selectedAgent || !currentServiceForContact) {
      showError('Veuillez entrer un message');
      return;
    }
    setContactClientLoading(true);
    try {
      const res = await AxiosInstance.post(`/api/agents/${selectedAgent._id}/services/${currentServiceForContact}/contact-client`, {
        subject: contactSubject,
        message: contactMessage,
        channel: contactChannel
      });
      const result = res.data;
      if (result.success) {
        showSuccess('Message envoyé au client avec succès');
        closeContactClientModal();
        setAgentServices(prev => prev.map(s => s.serviceDetailId === currentServiceForContact ? { ...s, contactNotesCount: (s.contactNotesCount || 0) + 1 } : s));
      } else {
        showError(result.message || 'Erreur lors de l\'envoi du message');
      }
    } catch (err) {
      console.error('Erreur envoi message client:', err);
      showError(err.response?.data?.message || 'Impossible d\'envoyer le message au client');
    } finally {
      setContactClientLoading(false);
    }
  }

  // ===== History =====
  async function loadAgentHistory() {
    if (!selectedAgent) return;
    setHistoryLoading(true);
    setHistoryItems([]);
    try {
      const res = await AxiosInstance.get(`/api/agents/${selectedAgent._id}/history`);
      const result = res.data;
      if (result.success && result.data.length > 0) {
        setHistoryItems(result.data);
        if (result.statistics) setHistoryStatistics(result.statistics);
      } else setHistoryItems([]);
    } catch (err) {
      console.error('Erreur historique:', err);
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  function switchTab(tabName) {
    setActiveTab(tabName);
    if (tabName === 'history' && selectedAgent) loadAgentHistory();
  }

  // ===== Utilitaires =====
  function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function formatFileSize(bytes) {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  const totalServices = (stats.totalActiveServices || 0) +
    allAgents.reduce((sum, agent) => sum + (agent.totalCompletedServices || 0), 0);

  return (
    <div className="main-contentt">
      <div className="headerr">
        <h1 style={{ fontWeight: '900' }} >👥 Gestion des Agents</h1>
        <button className="bttn bttn-primary" onClick={openAddModal} style={{ fontWeight: '900' }}>
          ➕ Ajouter un agent
        </button>
      </div>

      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total agents</div>
          <div className="stat-value">{stats.totalAgents ?? '-'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Agents actifs</div>
          <div className="stat-value" style={{ color: '#48bb78' }}>{stats.activeAgents ?? '-'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Services en cours</div>
          <div className="stat-value">{stats.totalActiveServices ?? '-'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Taux occupation moyen</div>
          <div className="stat-value">{stats.averageOccupation ? stats.averageOccupation + '%' : '-'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total services</div>
          <div className="stat-value" style={{ color: '#667eea' }}>{totalServices > 0 ? totalServices : '-'}</div>
        </div>
      </div>

      <div className="table-container">
        <div className="table-headerr">
          <input
            type="text"
            className="search-box"
            placeholder="🔍 Rechercher un agent..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <div className="filter-bar">
            <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">Tous les statuts</option>
              <option value="true">Actifs</option>
              <option value="false">Inactifs</option>
            </select>
            <select className="filter-select" value={specialtyFilter} onChange={e => setSpecialtyFilter(e.target.value)}>
              <option value="">Toutes les spécialités</option>
              <option value="mairie">Mairie</option>
              <option value="judiciaire">Judiciaire</option>
              <option value="all">Toutes</option>
            </select>
          </div>
        </div>

        {loadingAgents ? (
          <div id="loadingAgents" className="looading">
            <div className="spinner"></div>
            <span>Chargement des agents...</span>
          </div>
        ) : (
          <div id="agentsTableContainer">
            <table className="styled-table" >
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Services actifs</th>
                  <th>Total complétés</th>
                  <th>Note</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAgents.length === 0 ? (
                  <tr className="empty-row">
                    <td colSpan={8} className="emptye-state">
                      <div className="emptye-state-content">
                        <div className="emptye-state-icon">👥</div>
                        <div className="emptye-state-text">Aucun agent trouvé</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAgents.map(agent => {
                    const user = agent.userId || {};
                    const initials = getInitials(user.name || 'Agent');
                    const statusClass = agent.isActive ? 'status-active' : 'status-inactive';
                    const statusText = agent.isActive ? '● Active' : '● Inactive';
                    const hasActiveService = (agent.currentActiveServices || 0) > 0;
                    return (
                      <tr key={agent._id}>
                        <td>
                          <div className="agent-celle">
                            <div className="agent-avatarr">{initials}</div>
                            <div className="agent-info">
                              <div className="agent-name">
                                {user.name || 'N/A'}
                                {hasActiveService && (
                                  <span
                                    title="Service en cours"
                                    style={{
                                      display: 'inline-block',
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: '#48bb78',
                                      marginLeft: 8,
                                      boxShadow: '0 0 0 0 rgba(72, 187, 120, 0.7)',
                                      animation: 'pulse-green 1.5s infinite',
                                    }}
                                  />
                                )}
                              </div>
                              <div className="agent-id">Agent #{String(agent._id).slice(-6).toUpperCase()}</div>
                            </div>
                          </div>
                        </td>
                        <td>{user.email || 'N/A'}</td>
                        <td>{user.phone || 'N/A'}</td>
                        <td>
                          <strong style={{ color: '#667eea' }}>{agent.currentActiveServices || 0}</strong>
                          {hasActiveService && (
                            <span
                              style={{
                                display: 'inline-block',
                                marginLeft: 8,
                                padding: '2px 8px',
                                borderRadius: 12,
                                backgroundColor: '#fed7d7',
                                color: '#c53030',
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              En cours
                            </span>
                          )}
                        </td>
                        <td>{agent.totalCompletedServices || 0}</td>
                        <td>⭐ {agent.rating ? Number(agent.rating).toFixed(1) : '0.0'}/5</td>
                        <td><span className={`status-badge ${statusClass}`}>{statusText}</span></td>
                        <td>
                          <div className="actions-cell">
                            <button className="bttn bttn-secondary" onClick={() => openAgentModal(agent._id)}>👁️ Voir</button>
                            <button className="bttn bttn-secondary" onClick={(e) => openPriceModal(agent, e)} title="Modifier le tarif mandataire">💰 Tarif</button>
                            <button className="bttn bttn-secondary" onClick={openUpdateModal}>✏️</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Agent Modal */}
      {agentModalOpen && selectedAgent && (
        <div className="modale active" onClick={e => { if (e.target === e.currentTarget) closeAgentModal(); }}>
          <div className="modale-content" onClick={e => e.stopPropagation()}>
            <div className="modale-headerr">
              <div className="modal-headerr-info">
                <div className="modal-avatar" style={{ fontWeight: 900 }} >{getInitials(selectedAgent.userId?.name || 'Agent')}</div>
                <div>
                  <h2 id="modalAgentName" style={{ marginBottom: 5, fontSize: '25px', fontWeight: 900 }}>{selectedAgent.userId?.name || 'N/A'}</h2>
                  <div style={{ fontSize: 14, color: '#718096' }}>
                    <span id="modalAgentId">Agent #{String(selectedAgent._id).slice(-6).toUpperCase()}</span> •
                    <span id="modalAgentStatus">{selectedAgent.isActive ? '● Active' : '● Inactive'}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#718096', marginTop: 5 }}>
                    📧 {selectedAgent.userId?.email || 'N/A'} • 📱 {selectedAgent.userId?.phone || 'N/A'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'start', gap: 10 }}>
                <button className="bttn bttn-secondary">✏️ Modifier</button>
                <button className="bttn bttn-danger">🚫 {selectedAgent.isActive ? 'Désactiver' : 'Activer'}</button>
                <button className="close-modal" onClick={closeAgentModal}>&times;</button>
              </div>
            </div>

            <div className="modal-body">
              {agentLoading ? (
                <div id="modalLoading" className="looading" >
                  <div className="spinner"></div>
                  <span>Chargement des détails...</span>
                </div>
              ) : (
                <div id="modalContent">
                  <div className="modal-stats">
                    <div className="modal-stat-box">
                      <div className="modal-stat-value">{selectedAgent.currentActiveServices || 0}</div>
                      <div className="modal-stat-label">Services actifs</div>
                    </div>
                    <div className="modal-stat-box">
                      <div className="modal-stat-value">{agentStats?.totalCompleted ?? 0}</div>
                      <div className="modal-stat-label">Complétés</div>
                    </div>
                    <div className="modal-stat-box">
                      <div className="modal-stat-value">{agentStats ? (agentStats.rating ? Number(agentStats.rating).toFixed(1) : '0.0') : '-'}</div>
                      <div className="modal-stat-label">Note moyenne</div>
                    </div>
                    <div className="modal-stat-box">
                      <div className="modal-stat-value">{agentStats?.successRate ? agentStats.successRate + '%' : '-'}</div>
                      <div className="modal-stat-label">Taux réussite</div>
                    </div>
                    <div className="modal-stat-box">
                      <div className="modal-stat-value">{agentStats?.averageCompletionTime ? agentStats.averageCompletionTime + 'j' : '-'}</div>
                      <div className="modal-stat-label">Délai moyen</div>
                    </div>
                  </div>

                  <div className="tabs">
                    <button className={`tab ${activeTab === 'services' ? 'active' : ''}`} onClick={() => switchTab('services')}>
                      📋 Services (<span id="servicesCount">{agentServices.length}</span>)
                    </button>
                    <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => switchTab('info')}>ℹ️ Informations</button>
                    <button className={`tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => switchTab('history')}>📊 Historique</button>
                  </div>

                  <div className={`tab-contentt ${activeTab === 'services' ? 'active' : ''}`} id="tab-services">
                    <div className="section-title">📋 Services assignés</div>
                    <div className="filter-bar" style={{ marginBottom: 20 }}>
                      <select className="filter-select" id="serviceStatusFilter" onChange={e => {
                        const v = e.target.value;
                        if (!v) loadAgentServices(selectedAgent._id);
                        else setAgentServices(prev => prev.filter(s => s.agentStatus === v));
                      }}>
                        <option value="">Tous les statuts</option>
                        <option value="not_started">Pas démarré</option>
                        <option value="documents_collection">Collecte documents</option>
                        <option value="in_progress">En cours</option>
                        <option value="completed">Complété</option>
                        <option value="delivered">Livré</option>
                      </select>
                    </div>

                    {agentServices.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-state-icon">📋</div>
                        <div>Aucun service trouvé</div>
                      </div>
                    ) : (
                      <div>
                        {agentServices.map(service => {
                          const statusColors = {
                            'not_started': { bg: '#fed7d7', color: '#c53030', icon: '🔴', text: 'Pas démarré' },
                            'documents_collection': { bg: '#feebc8', color: '#c05621', icon: '📄', text: 'Collecte documents' },
                            'in_progress': { bg: '#bee3f8', color: '#2c5282', icon: '⚙️', text: 'En cours' },
                            'completed': { bg: '#c6f6d5', color: '#2f855a', icon: '✅', text: 'Complété' },
                            'delivered': { bg: '#b2f5ea', color: '#234e52', icon: '📦', text: 'Livré' }
                          };
                          const statusInfo = statusColors[service.agentStatus] || statusColors['not_started'];
                          const progress = service.progress || {};
                          const progressPercent = progress.percentage || 0;

                          return (
                            <div key={service.serviceDetailId} className={`service-card ${expandedServices[service.serviceDetailId] ? 'expanded' : ''}`} onClick={() => toggleServiceExpand(service.serviceDetailId)}>
                              <div className="service-headerr">
                                <div>
                                  <div className="service-title">{service.serviceInfo?.title || 'Service'}</div>
                                  <div style={{ fontSize: 13, color: '#718096', marginTop: 3 }}>{service.serviceInfo?.reference || 'N/A'}</div>
                                </div>
                                <span className="status-badge" style={{ background: statusInfo.bg, color: statusInfo.color }}>{statusInfo.icon} {statusInfo.text}</span>
                              </div>

                              <div className="servicee-meta">
                                <span>👤 <strong>{service.client?.name || 'N/A'}</strong></span>
                                <span>📍 {service.location?.fullLocation || 'N/A'}</span>
                                <span>📅 Assigné le {formatDate(service.assignedAt)}</span>
                              </div>

                              <div style={{ marginTop: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                                  <span>Progression</span>
                                  <span style={{ color: '#667eea', fontWeight: 600 }}>{progress.etapesWithDocuments || 0}/{progress.totalEtapes || 0} étapes ({progressPercent}%)</span>
                                </div>
                                <div className="progress-bar">
                                  <div className="progress-fill" style={{ width: `${progressPercent}%` }}></div>
                                </div>
                              </div>

                              {expandedServices[service.serviceDetailId] && (
                                <div className="service-details">
                                  <h4 style={{ marginBottom: 15, fontSize: '16px', fontWeight: 900 }}>👤 Informations Client</h4>
                                  <div className="info-box">
                                    <div className="info-row"><div className="info-label">Nom complet</div><div className="info-value">{service.client?.name || 'N/A'}</div></div>
                                    <div className="info-row"><div className="info-label">Email</div><div className="info-value">{service.client?.email || 'N/A'}</div></div>
                                    <div className="info-row"><div className="info-label">Téléphone</div><div className="info-value">{service.client?.phone || 'N/A'}</div></div>
                                    <div className="info-row"><div className="info-label">Localisation</div><div className="info-value">{service.location?.fullLocation || 'N/A'}</div></div>
                                    <div className="info-row"><div className="info-label">Date assignation</div><div className="info-value">{formatDate(service.assignedAt)}</div></div>
                                    {service.daysRemaining !== null && (
                                      <div className="info-row"><div className="info-label">Échéance</div><div className="info-value" style={{ color: service.isOverdue ? '#e53e3e' : '#667eea', fontWeight: 600 }}>{service.isOverdue ? 'En retard' : service.daysRemaining + ' jours restants'}</div></div>
                                    )}
                                  </div>
                                  {/* Documents par étape */}
                                  <div>
                                    <h4 style={{ margin: '20px 0 15px', fontSize: '16px', fontWeight: 900 }}>📁 Documents par Étape</h4>
                                    {serviceDetailsCache[service.serviceDetailId]?.etapesDetails?.length > 0 ? (
                                      serviceDetailsCache[service.serviceDetailId].etapesDetails.map(etape => (
                                        <div key={etape.etapeId} className="etape-section" style={{ ...(etape.documentsCount > 0 ? {} : { background: '#fff5f5', border: '1px dashed #fc8181' }) }}>
                                          <div className="etape-headerr"><span>{etape.documentsCount > 0 ? '✅' : '⚠️'} {etape.etapeTitre}</span><span style={{ color: etape.documentsCount > 0 ? '#48bb78' : '#e53e3e' }}>{etape.documentsCount} document(s)</span></div>
                                          {etape.documentsCount > 0 ? (
                                            <div id={`etape-docs-${etape.etapeId}`} style={{ marginTop: 10 }}>
                                              {serviceDetailsCache[service.serviceDetailId]?.documents?.[etape.etapeId] ? (
                                                serviceDetailsCache[service.serviceDetailId].documents[etape.etapeId].map(doc => (
                                                  <div key={doc._id} className="document-item">
                                                    <div className="document-info">
                                                      <div className="file-icon">📄</div>
                                                      <div>
                                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{doc.originalName}</div>
                                                        <div style={{ fontSize: 12, color: '#718096' }}>{formatFileSize(doc.fileSize)} • {formatDate(doc.uploadedAt)}</div>
                                                      </div>
                                                    </div>
                                                    <button className="bttn bttn-secondary" onClick={() => downloadDocument(doc._id)}>⬇ Télécharger</button>
                                                  </div>
                                                ))
                                              ) : (
                                                <button className="bttn bttn-secondary" onClick={(e) => { e.stopPropagation(); loadEtapeDocuments(etape.etapeId, service.serviceDetailId); }} style={{ width: '100%' }}>📄 Voir les documents</button>
                                              )}
                                            </div>
                                          ) : (
                                            <div style={{ color: '#c53030', fontSize: 13, padding: 10 }}>En attente - Documents non uploadés par le client</div>
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      <div style={{ padding: 10, color: '#718096' }}>Aucun document</div>
                                    )}

                                    <button className="bttn bttn-primary" onClick={(e) => { e.stopPropagation(); downloadAllDocuments(service.serviceDetailId); }} style={{ width: '100%', marginTop: 15 }}>📥 Télécharger tous les documents (ZIP)</button>
                                  </div>

                                  <h4 style={{ margin: '20px 0 15px', fontSize: '16px', fontWeight: 900 }}>📝 Notes de l'agent</h4>
                                  <div className="info-box"><div style={{ padding: 12, background: '#f7fafc', borderRadius: 6, marginBottom: 12 }}><p style={{ fontSize: 14, color: '#2d3748', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{service.agentNotes || 'Aucune note pour le moment'}</p></div></div>

                                  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                                    <button className="bttn bttn-secondary" onClick={(e) => openContactNotesModal(service.serviceDetailId, e)} style={{ flex: 1 }}>💬 Notes contact ({service.contactNotesCount || 0})</button>
                                    <button className="bttn bttn-secondary" onClick={(e) => openContactClientModal(service.serviceDetailId, e)} style={{ flex: 1 }}>📧 Contacter client</button>
                                    <button className="bttn bttn-primary" onClick={(e) => openUpdateStatusModal(service.serviceDetailId, e)} style={{ flex: 1, fontWeight: '900' }}>🔄 Changer statut</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className={`tab-contentt ${activeTab === 'info' ? 'active' : ''}`} id="tab-info">
                    <div className="section-title">ℹ️ Informations de l'agent</div>
                    <div className="info-box">
                      <div className="info-row"><div className="info-label">Nom complet</div><div className="info-value">{selectedAgent.userId?.name || 'N/A'}</div></div>
                      <div className="info-row"><div className="info-label">ID Agent</div><div className="info-value">{String(selectedAgent._id).slice(-6).toUpperCase()}</div></div>
                      <div className="info-row"><div className="info-label">Email</div><div className="info-value">{selectedAgent.userId?.email || 'N/A'}</div></div>
                      <div className="info-row"><div className="info-label">Téléphone</div><div className="info-value">{selectedAgent.userId?.phone || 'N/A'}</div></div>
                      <div className="info-row"><div className="info-label">Statut</div><div className="info-value"><span style={{ color: selectedAgent.isActive ? '#48bb78' : '#c53030', fontWeight: 600 }}>● {selectedAgent.isActive ? 'Active' : 'Inactive'}</span></div></div>
                      <div className="info-row"><div className="info-label">Spécialités</div><div className="info-value">{(selectedAgent.specialties || []).map(s => <span key={s} className="status-badge" style={{ marginRight: 6 }}>{s}</span>) || 'N/A'}</div></div>
                      <div className="info-row"><div className="info-label">Expérience</div><div className="info-value">{selectedAgent.experience || 0} ans</div></div>
                      <div className="info-row"><div className="info-label">Capacité max</div><div className="info-value">{selectedAgent.maxConcurrentServices || 5} services simultanés</div></div>
                      <div className="info-row"><div className="info-label">Tarif mandataire (XAF)</div><div className="info-value">{formatXaf(selectedAgent.basePrice ?? DEFAULT_AGENT_PRICE_XAF)} <span style={{ color: '#718096', fontSize: 13 }}>({formatEurFromXaf(selectedAgent.basePrice ?? DEFAULT_AGENT_PRICE_XAF)} indicatif)</span></div></div>
                    </div>

                    <h4 style={{ margin: '30px 0 15px', fontSize: '16px', fontWeight: 900 }}>📊 Statistiques de performance</h4>
                    <div className="info-box">
                      <div className="info-row"><div className="info-label">Note moyenne</div><div className="info-value">⭐ {agentStats ? (agentStats.rating ? Number(agentStats.rating).toFixed(1) : '0.0') : '0.0'}/5</div></div>
                      <div className="info-row"><div className="info-label">Taux de réussite</div><div className="info-value" style={{ color: '#48bb78', fontWeight: 600 }}>{agentStats?.successRate ?? 0}%</div></div>
                      <div className="info-row"><div className="info-label">Délai moyen de traitement</div><div className="info-value">{agentStats?.averageCompletionTime ?? 0} jours</div></div>
                      <div className="info-row"><div className="info-label">Services complétés</div><div className="info-value">{agentStats?.totalCompleted ?? 0} services</div></div>
                      <div className="info-row"><div className="info-label">Services en cours</div><div className="info-value">{selectedAgent.currentActiveServices || 0} services</div></div>
                      <div className="info-row"><div className="info-label">Taux d'occupation</div><div className="info-value">{agentStats?.currentLoad ?? '0/0'}</div></div>
                    </div>
                  </div>

                  <div className={`tab-contentt ${activeTab === 'history' ? 'active' : ''}`} id="tab-history">
                    <div className="section-title">📊 Historique des services complétés</div>
                    {historyLoading ? (
                      <div className="looading"><div className="spinner"></div><span>Chargement...</span></div>
                    ) : (
                      <div>
                        {historyItems.length === 0 ? (
                          <div className="empty-state"><div className="empty-state-icon">📊</div><div>Aucun service complété</div></div>
                        ) : (
                          <div>
                            {historyItems.map(item => (
                              <div key={item._id} style={{ background: '#f7fafc', padding: 20, borderRadius: 8, marginBottom: 15, borderLeft: '4px solid #48bb78' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                                  <div>
                                    <div style={{ fontWeight: 600, marginBottom: 5 }}>📄 {item.serviceTitle}</div>
                                    <div style={{ fontSize: 13, color: '#718096' }}>Client: {item.clientName} • {item.serviceReference}</div>
                                  </div>
                                  <span className="status-badge" style={{ background: '#c6f6d5', color: '#2f855a' }}>✅ Complété</span>
                                </div>
                                <div style={{ fontSize: 13, color: '#718096' }}>📅 {formatDate(item.startDate)} - {formatDate(item.completedDate)} • ⏱️ Durée: {item.durationDays} jours {item.rating ? ` • ⭐ Note: ${item.rating}/5` : ''}</div>
                              </div>
                            ))}

                            {historyStatistics && (
                              <div style={{ background: 'white', padding: 20, borderRadius: 8, border: '2px solid #e2e8f0', marginTop: 20 }}>
                                <h4 style={{ marginBottom: 15 }}>📊 Statistiques de l'historique</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 15 }}>
                                  <div>
                                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#667eea' }}>{historyStatistics.totalCompleted}</div>
                                    <div style={{ fontSize: 12, color: '#718096' }}>Total complétés</div>
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#667eea' }}>{historyStatistics.averageDuration}j</div>
                                    <div style={{ fontSize: 12, color: '#718096' }}>Durée moyenne</div>
                                  </div>
                                  {historyStatistics.averageRating && (
                                    <div>
                                      <div style={{ fontSize: 24, fontWeight: 'bold', color: '#667eea' }}>{historyStatistics.averageRating}</div>
                                      <div style={{ fontSize: 12, color: '#718096' }}>Note moyenne</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contact Notes Modal */}
      {contactNotesOpen && (
        <div className="modale active" onClick={(e) => { if (e.target === e.currentTarget) closeContactNotesModal(); }}>
          <div className="modale-content" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
            <div className="modale-headerr">
              <div>
                <h2 style={{ fontWeight: '900', fontSize: '23px' }} >💬 Notes de Contact</h2>
                <div style={{ fontSize: 14, color: '#718096', marginTop: 5 }}>Service: {agentServices.find(s => s.serviceDetailId === currentServiceForNotes)?.serviceInfo?.title || '-'}</div>
              </div>
              <button className="close-modal" onClick={closeContactNotesModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Ajouter une note</label>
                <textarea className="form-textarea" value={newContactNote} onChange={e => setNewContactNote(e.target.value)} placeholder="Entrez votre note..."></textarea>
              </div>
              <button className="bttn bttn-primary" onClick={addContactNote} style={{ width: '100%', marginBottom: 20, fontWeight: '900' }}  >➕ Ajouter la note</button>

              <div className="section-title" style={{ marginTop: 30 }}>📝 Historique des notes</div>
              {contactNotesLoading ? (
                <div className="looading"><div className="spinner"></div><span>Chargement...</span></div>
              ) : (
                <div className="notes-list">
                  {contactNotes.length === 0 ? (
                    <div className="empty-state"><div className="empty-state-icon">💬</div><div>Aucune note de contact</div></div>
                  ) : contactNotes.map(note => (
                    <div key={note._id} className="note-item">
                      <div className="note-headerr"><span>{formatDate(note.date)}</span><span>Par: {note.addedBy?.name || 'Agent'}</span></div>
                      <div className="note-text">{note.note}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {updateStatusOpen && (
        <div className="modale active" onClick={(e) => { if (e.target === e.currentTarget) closeUpdateStatusModal(); }}>
          <div className="modale-content" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modale-headerr">
              <h2 style={{ fontWeight: '900', fontSize: '23px' }}>🔄 Mettre à jour le statut</h2>
              <button className="close-modal" onClick={closeUpdateStatusModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">Service</label><div id="updateStatusServiceTitle" style={{ fontWeight: 600, color: '#1a202c' }}>{agentServices.find(s => s.serviceDetailId === currentServiceForStatus)?.serviceInfo?.title || '-'}</div></div>

              <div className="form-group">
                <label className="form-label">Nouveau statut</label>
                <select className="form-select" value={newServiceStatus} onChange={e => setNewServiceStatus(e.target.value)}>
                  <option value="not_started">🔴 Pas démarré</option>
                  <option value="documents_collection">📄 Collecte documents</option>
                  <option value="in_progress">⚙️ En cours</option>
                  <option value="completed">✅ Complété</option>
                  <option value="delivered">📦 Livré</option>
                </select>
              </div>

              <div className="form-group"><label className="form-label">Notes (optionnel)</label><textarea className="form-textarea" value={statusUpdateNotes} onChange={e => setStatusUpdateNotes(e.target.value)} placeholder="Ajoutez des notes sur ce changement de statut..."></textarea></div>

              <button className="bttn bttn-primary" onClick={submitStatusUpdate} style={{ width: '100%', fontWeight: '900' }}>💾 Enregistrer le changement</button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Client Modal */}
      {contactClientOpen && (
        <div className="modale active" onClick={(e) => { if (e.target === e.currentTarget) closeContactClientModal(); }}>
          <div className="modale-content" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modale-headerr">
              <h2 style={{ fontWeight: '900', fontSize: '23px' }}>📧 Contacter le client</h2>
              <button className="close-modal" onClick={closeContactClientModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Service</label>
                <div style={{ fontWeight: 600, color: '#1a202c' }}>{agentServices.find(s => s.serviceDetailId === currentServiceForContact)?.serviceInfo?.title || '-'}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Canal</label>
                <select className="form-select" value={contactChannel} onChange={e => setContactChannel(e.target.value)}>
                  <option value="email">📧 Email</option>
                  <option value="whatsapp">💬 WhatsApp</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Sujet</label>
                <input type="text" className="form-input" value={contactSubject} onChange={e => setContactSubject(e.target.value)} placeholder="Sujet du message..." />
              </div>

              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea className="form-textarea" value={contactMessage} onChange={e => setContactMessage(e.target.value)} placeholder="Rédigez votre message au client..." rows={6}></textarea>
              </div>

              <button className="bttn bttn-primary" onClick={submitContactClient} disabled={contactClientLoading} style={{ width: '100%', fontWeight: '900' }}>
                {contactClientLoading ? '⏳ Envoi en cours...' : '📤 Envoyer le message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tarif Mandataire Modal */}
      {priceModalOpen && priceAgent && (
        <div className="modale active" onClick={(e) => { if (e.target === e.currentTarget) closePriceModal(); }}>
          <div className="modale-content" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modale-headerr">
              <h2 style={{ fontWeight: '900', fontSize: '23px' }}>💰 Modifier le tarif</h2>
              <button className="close-modal" onClick={closePriceModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Mandataire</label>
                <div style={{ fontWeight: 600, color: '#1a202c' }}>{priceAgent.userId?.name || 'N/A'}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Tarif mandataire (XAF) *</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="form-input"
                  value={priceInput}
                  onChange={e => handlePriceInputChange(e.target.value)}
                  placeholder="Ex : 8855"
                />
                {parsedPrice.value !== undefined && (
                  <div style={{ marginTop: 6, fontSize: 14, color: '#4a5568' }}>
                    {formatEurFromXaf(parsedPrice.value)} (indicatif)
                  </div>
                )}
                {priceError && (
                  <div style={{ marginTop: 6, fontSize: 13, color: '#c53030', fontWeight: 600 }}>{priceError}</div>
                )}
              </div>

              <div style={{ fontSize: 12, color: '#718096', marginBottom: 15 }}>
                Le tarif est stocké en francs CFA (XAF). Parité fixe : 1 € = 655,957 XAF. L'équivalent en euros est affiché à titre indicatif.
              </div>

              <button
                className="bttn bttn-primary"
                onClick={submitPriceUpdate}
                disabled={priceLoading}
                style={{ width: '100%', fontWeight: '900' }}
              >
                {priceLoading ? '⏳ Enregistrement...' : '💾 Enregistrer le tarif'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddModalOpen && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal-content" style={modalStyle}>
            <h2 style={{ fontWeight: "800", fontSize: "20px" }}>➕ Ajouter un agent</h2>
            <form>
              <label>Nom complet :</label>
              <input type="text" placeholder="Jean Dupont" style={inputStyle} />


              <label>Date de naissance :</label>
              <input type="date" placeholder="JJ/MM/AAAA" style={inputStyle} />

              <label>Email :</label>
              <input type="email" placeholder="jean@example.com" style={inputStyle} />

              <label>Téléphone :</label>
              <input type="tel" placeholder="06 00 00 00 00" style={inputStyle} />

              <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button type="submit" className="bttn bttn-primary">✅ Enregistrer</button>
                <button type="button" className="bttn bttn-secondary" onClick={closeAddModal}>❌ Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {isUpdateModalOpen && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal-content" style={modalStyle}>
            <h2 style={{ fontWeight: "800", fontSize: "20px" }}>📝 Modifier un agent</h2>
            <form>
              <label>Nom complet :</label>
              <input type="text" placeholder="Jean Dupont" style={inputStyle} />


              <label>Date de naissance :</label>
              <input type="date" placeholder="JJ/MM/AAAA" style={inputStyle} />

              <label>Email :</label>
              <input type="email" placeholder="jean@example.com" style={inputStyle} />

              <label>Téléphone :</label>
              <input type="tel" placeholder="06 00 00 00 00" style={inputStyle} />

              <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button type="submit" className="bttn bttn-primary">📝 Modifier</button>
                <button type="button" className="bttn bttn-secondary" onClick={closeUpdateModal}>❌ Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
